import React from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Outlet } from 'react-router-dom';
import theme from './theme';

import { Spinner, SkeletonProvider } from '@pillexa-dev/ui';
import { AuthProvider } from './context/auth-context';
import { RoleProvider } from './context/role-context';
import DatadogErrorBoundary from './components/datadog-error-boundary';
import { initDatadogRUM, setUserContext, trackFeatureFlags } from './utils/datadog-rum';
import { useFeatureFlags } from './providers/feature-flag-provider-v2';
import { datadogRum } from '@datadog/browser-rum';
import { useAuthContext } from './context/auth-context';
import { setupGlobalErrorHandlers } from './utils/error-handler';

const NotFound = lazy(() => import('./pages/404'));
const Dashboard = lazy(() => import('./pages/a'));
const IndexPage = lazy(() => import('./pages/index'));
const InvitePage = lazy(() => import('./pages/invite'));
const LoginPage = lazy(() => import('./pages/login'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password'));
const UnauthorizedPage = lazy(() => import('./pages/unauthorized'));

const AppWithDatadog = () => (
  <>
    <DatadogRUMInitializer />
    <Outlet />
  </>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<AppWithDatadog />}>
      <Route index element={<IndexPage />} />
      <Route path="a/*" element={<Dashboard />} />
      <Route path="invite" element={<InvitePage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="unauthorized" element={<UnauthorizedPage />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  ),
  {
    basename: __BASE__,
  }
);

const Loading = () => (
  <div className="bg-grey-5 text-grey-90 flex h-screen w-full items-center justify-center">
    <Spinner variant="secondary" />
  </div>
);

const DatadogRUMInitializer = () => {
  const { user, isLoading } = useAuthContext();
  const { flags } = useFeatureFlags();

  React.useEffect(() => {
    if (!isLoading) {
      initDatadogRUM();
      if (user && user.id) {
        setUserContext(user);
      } else {
        datadogRum.setUser({});
      }
    }
  }, [isLoading, user]);

  React.useEffect(() => {
    if (flags) {
      trackFeatureFlags(flags);
    }
  }, [flags]);

  if (isLoading) {
    return <Loading />;
  }
  return null;
};

const App = () => {
  // Initialize global error handlers
  React.useEffect(() => {
    setupGlobalErrorHandlers();
  }, []);

  return (
    <DatadogErrorBoundary>
      <Suspense fallback={<Loading />}>
        <ChakraProvider theme={theme}>
          <SkeletonProvider>
            <AuthProvider>
              <RoleProvider>
                <RouterProvider router={router} />
              </RoleProvider>
            </AuthProvider>
          </SkeletonProvider>
        </ChakraProvider>
      </Suspense>
    </DatadogErrorBoundary>
  );
};

export default App;
// Test comment
